import React, { useContext, useEffect, useRef, useState } from 'react';

import StarFilled from '@amzn/meridian-tokens/base/icon/star-filled';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';

import { useHistory } from 'react-router-dom';
import { AppContext } from 'src/contexts/appContext';
import { Favorite, getFavorites, removeFavorite, reorderFavorites } from 'src/util/favorites';
import { SearchResult, searchSite } from 'src/util/search';

import './Home.scss';

export const Home: React.FC = () => {
    const history = useHistory();
    const { entities } = useContext(AppContext);
    const [searchQuery, setSearchQuery] = useState('');
    const [favorites, setFavorites] = useState<Favorite[]>([]);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const [foldAngle, setFoldAngle] = useState(180);
    const [animationComplete, setAnimationComplete] = useState(false);

    useEffect(() => {
        setFavorites(getFavorites());
        const foldTimer = setTimeout(() => setFoldAngle(0), 300);
        const completeTimer = setTimeout(() => setAnimationComplete(true), 1200);
        return () => {
            clearTimeout(foldTimer);
            clearTimeout(completeTimer);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        const results = searchSite(value, entities);
        setSearchResults(results);
        setShowResults(results.length > 0);
    };

    const handleSearchResultClick = (path: string) => {
        history.push(path);
        setSearchQuery('');
        setShowResults(false);
    };

    const quickActions = favorites.slice(0, 3).map((fav) => ({
        title: fav.title,
        description: fav.description,
        onClick: () => history.push(fav.path)
    }));

    const handleDragStart = (e: React.DragEvent, index: number) => {
        e.dataTransfer.effectAllowed = 'move';
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedIndex === null) return;
        setDropTargetIndex(index);
    };

    const handleDrop = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newFavorites = [...favorites];
        const draggedItem = newFavorites[draggedIndex];
        newFavorites.splice(draggedIndex, 1);
        newFavorites.splice(index, 0, draggedItem);

        setFavorites(newFavorites);
        reorderFavorites(newFavorites);
        setDraggedIndex(null);
        setDropTargetIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDropTargetIndex(null);
    };

    const handleRemove = (e: React.MouseEvent, path: string) => {
        e.stopPropagation();
        removeFavorite(path);
        setFavorites(getFavorites());
    };

    return (
        <div className="home-container">
            <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                    <filter id="inset-shadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feComponentTransfer in="SourceAlpha">
                            <feFuncA type="table" tableValues="1 0" />
                        </feComponentTransfer>
                        <feGaussianBlur stdDeviation="3" />
                        <feOffset dx="2" dy="2" result="shadow" />
                        <feFlood floodColor="black" floodOpacity="0.6" />
                        <feComposite in2="shadow" operator="in" />
                        <feComposite in2="SourceAlpha" operator="in" />
                        <feMerge>
                            <feMergeNode in="SourceGraphic" />
                            <feMergeNode />
                        </feMerge>
                    </filter>
                    <filter id="inset-shadow-light" x="-50%" y="-50%" width="200%" height="200%">
                        <feComponentTransfer in="SourceAlpha">
                            <feFuncA type="table" tableValues="1 0" />
                        </feComponentTransfer>
                        <feGaussianBlur stdDeviation="1" />
                        <feOffset dx="1" dy="1" result="shadow" />
                        <feFlood floodColor="black" floodOpacity="0.2" />
                        <feComposite in2="shadow" operator="in" />
                        <feComposite in2="SourceAlpha" operator="in" />
                        <feMerge>
                            <feMergeNode in="SourceGraphic" />
                            <feMergeNode />
                        </feMerge>
                    </filter>
                    <filter id="inset-shadow-medium" x="-50%" y="-50%" width="200%" height="200%">
                        <feComponentTransfer in="SourceAlpha">
                            <feFuncA type="table" tableValues="1 0" />
                        </feComponentTransfer>
                        <feGaussianBlur stdDeviation="2" />
                        <feOffset dx="1.5" dy="1.5" result="shadow" />
                        <feFlood floodColor="black" floodOpacity="0.4" />
                        <feComposite in2="shadow" operator="in" />
                        <feComposite in2="SourceAlpha" operator="in" />
                        <feMerge>
                            <feMergeNode in="SourceGraphic" />
                            <feMergeNode />
                        </feMerge>
                    </filter>
                    <filter id="inset-shadow-top" x="-50%" y="-50%" width="200%" height="200%">
                        <feComponentTransfer in="SourceAlpha">
                            <feFuncA type="table" tableValues="1 0" />
                        </feComponentTransfer>
                        <feGaussianBlur stdDeviation="3" />
                        <feOffset dx="2" dy="-2" result="shadow" />
                        <feFlood floodColor="black" floodOpacity="0.6" />
                        <feComposite in2="shadow" operator="in" />
                        <feComposite in2="SourceAlpha" operator="in" />
                        <feMerge>
                            <feMergeNode in="SourceGraphic" />
                            <feMergeNode />
                        </feMerge>
                    </filter>
                    <filter id="inset-shadow-bottom" x="-50%" y="-50%" width="200%" height="200%">
                        <feComponentTransfer in="SourceAlpha">
                            <feFuncA type="table" tableValues="1 0" />
                        </feComponentTransfer>
                        <feGaussianBlur stdDeviation="3" />
                        <feOffset dx="2" dy="2" result="shadow" />
                        <feFlood floodColor="black" floodOpacity="0.6" />
                        <feComposite in2="shadow" operator="in" />
                        <feComposite in2="SourceAlpha" operator="in" />
                        <feMerge>
                            <feMergeNode in="SourceGraphic" />
                            <feMergeNode />
                        </feMerge>
                    </filter>
                </defs>
            </svg>
            <div className="home-hero">
                <div
                    style={{
                        height: '152px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        marginBottom: '4px'
                    }}
                >
                    {animationComplete ? (
                        <div className="origami-title-complete">
                            <Heading level={1}>ORIGAMI</Heading>
                            <div className="shadow-overlay">
                                <Heading level={1}>ORIGAMI</Heading>
                            </div>
                        </div>
                    ) : (
                        <div style={{ perspective: 1000, marginBottom: '16px' }}>
                            <div
                                className="origami-title-top"
                                style={{
                                    transform: `rotateX(${foldAngle}deg)`,
                                    transition: 'transform 0.9s ease-out'
                                }}
                            >
                                <div className="origami-title-back" />
                                ORIGAMI
                            </div>
                            <div className="origami-title-bottom">ORIGAMI</div>
                        </div>
                    )}
                </div>
                <p className="home-subtitle">Music&#39;s all in one contracts, reporting, and royalties platform.</p>
                <div className="home-search" ref={searchRef}>
                    <SearchField
                        placeholder="search here ..."
                        value={searchQuery}
                        onChange={(value) => handleSearchChange(value)}
                        onFocus={() => searchQuery && setShowResults(true)}
                        onSubmit={() => {}}
                    />
                    {showResults && searchResults.length > 0 && (
                        <div className="search-results">
                            {searchResults.map((result, index) => (
                                <div
                                    key={index}
                                    className="search-result-item"
                                    onClick={() => handleSearchResultClick(result.path)}
                                >
                                    <div className="search-result-main">
                                        <span className="search-result-title">{result.title}</span>
                                        {result.subtitle && (
                                            <span className="search-result-subtitle">{result.subtitle}</span>
                                        )}
                                    </div>
                                    <span className="search-result-category">{result.category}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="home-content">
                {quickActions.length > 0 && (
                    <div className="quick-actions">
                        {quickActions.map((action, index) => (
                            <div key={index} className="quick-action-card">
                                <Card onClick={action.onClick} spacingInset="none">
                                    <div className="card-gradient-header">
                                        <Text color={'inverted'}>{action.description}</Text>
                                    </div>
                                    <div className="card-white-section">
                                        <Text>{action.title}</Text>
                                    </div>
                                </Card>
                            </div>
                        ))}
                    </div>
                )}

                <Box className="favorites-section">
                    <h2 className="favorites-title">
                        <Icon tokens={StarFilled} /> Favorites
                    </h2>
                    {favorites.length > 0 ? (
                        <div className="favorites-grid">
                            {favorites.map((favorite, index) => (
                                <div
                                    key={index}
                                    className={`favorite-link ${draggedIndex === index ? 'dragging' : ''} ${
                                        dropTargetIndex === index && draggedIndex !== index ? 'drop-target' : ''
                                    }`}
                                    onClick={() => history.push(favorite.path)}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    onDragLeave={() => setDropTargetIndex(null)}
                                >
                                    <span className="drag-handle" onClick={(e) => e.stopPropagation()}>
                                        ⋮⋮
                                    </span>
                                    <span className="favorite-title">{favorite.title}</span>
                                    <button className="remove-btn" onClick={(e) => handleRemove(e, favorite.path)}>
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="favorites-empty">
                            No favorites yet. Add favorites from any page using the star icon.
                        </p>
                    )}
                </Box>
            </div>
        </div>
    );
};
